'use client';

import { useState, useEffect, useRef } from 'react';
import { useInView } from '@/lib/useInView';

interface ImpactMetric {
  value: string;
  description: string;
  eyebrow?: string;
  noAnimation?: boolean;
}

interface ImpactMetricsProps {
  metrics: ImpactMetric[];
  className?: string;
  columns?: 3 | 4;
}

interface UseCountUpOptions {
  /**
   * The target number to count up to
   */
  target: number;
  /**
   * Duration of the animation in milliseconds
   * @default 2000
   */
  duration?: number;
  /**
   * Delay before starting the animation in milliseconds
   * @default 0
   */
  delay?: number;
  /**
   * Easing function for the animation
   * @default 'easeOutCubic'
   */
  easing?: 'linear' | 'easeOutCubic' | 'easeInOutCubic';
}

/**
 * Hook for animating a number from 0 to a target value
 * 
 * @example
 * ```tsx
 * const count = useCountUp({ target: 82, duration: 2000 });
 * return <div>{count}%</div>;
 * ```
 */
const useCountUp = ({
  target,
  duration = 2000,
  delay = 0,
  easing = 'easeOutCubic',
  shouldStart = false
}: UseCountUpOptions & { shouldStart: boolean }): number => {
  const [count, setCount] = useState(0);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (!shouldStart) return;

    const easingFunctions = {
      linear: (t: number) => t,
      easeOutCubic: (t: number) => 1 - Math.pow(1 - t, 3),
      easeInOutCubic: (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
    };

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easingFunctions[easing](progress);
      const currentCount = Math.floor(easedProgress * target);

      setCount(currentCount);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setCount(target);
        animationFrameRef.current = null;
      }
    };

    const startAnimation = () => {
      const timeoutId = setTimeout(() => {
        startTimeRef.current = null;
        animationFrameRef.current = requestAnimationFrame(animate);
      }, delay);

      return timeoutId;
    };

    const timeoutId = startAnimation();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [shouldStart, target, duration, delay, easing]);

  return count;
};

/**
 * Parses a metric value string to extract the numeric value and formatting
 * 
 * @example
 * parseMetricValue("82%") // { number: 82, suffix: "%" }
 * parseMetricValue("1.5M") // { number: 1.5, suffix: "M" }
 * parseMetricValue("50K") // { number: 50, suffix: "K" }
 */
const parseMetricValue = (value: string): { number: number; prefix: string; suffix: string } => {
  // Match number (including decimals) and any prefix/suffix
  const match = value.match(/^([^\d]*)([\d.]+)([^\d]*)$/);
  
  if (!match) {
    // If no number found, return 0 with original value as suffix
    return { number: 0, prefix: '', suffix: value };
  }

  const [, prefix, numberStr, suffix] = match;
  const number = parseFloat(numberStr);

  return {
    number: isNaN(number) ? 0 : number,
    prefix: prefix || '',
    suffix: suffix || ''
  };
};


interface CountUpMetricProps {
  value: string;
  description: string;
  index: number;
  eyebrow?: string;
  hasAnyEyebrow: boolean;
  noAnimation?: boolean;
  prefersReducedMotion?: boolean;
}

const CountUpMetric = ({ value, description, index, eyebrow, hasAnyEyebrow, noAnimation, prefersReducedMotion = false }: CountUpMetricProps) => {
  const [shouldStart, setShouldStart] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);
  const { number: targetNumber, prefix, suffix } = parseMetricValue(value);
  
  // Skip animation if reduced motion is preferred or noAnimation is true
  const shouldAnimate = !prefersReducedMotion && !noAnimation;
  
  const count = useCountUp({
    target: targetNumber,
    duration: 2000,
    delay: index * 150, // Stagger animation for each metric
    easing: 'easeOutCubic',
    shouldStart: shouldAnimate ? shouldStart : false
  });

  // Format the number to preserve decimals if original had them
  const formatNumber = (num: number): string => {
    const originalMatch = value.match(/[\d.]+/);
    if (originalMatch) {
      const originalNum = parseFloat(originalMatch[0]);
      const hasDecimals = originalNum % 1 !== 0;
      if (hasDecimals) {
        return num.toFixed(1);
      }
    }
    return Math.floor(num).toString();
  };

  useEffect(() => {
    // If reduced motion or noAnimation, skip intersection observer
    if (!shouldAnimate) {
      return;
    }
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !shouldStart) {
            setShouldStart(true);
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px'
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [shouldStart, shouldAnimate]);

  return (
    <div ref={elementRef} className="min-w-0">
      {/* Mobile: only show eyebrow space if this metric has one */}
      {/* Desktop: show eyebrow space for all if any metric has one (for alignment) */}
      {eyebrow ? (
        <div className="min-h-[2rem] mb-0 lg:mb-3">
          <div className="eyebrow-lg text-black">
            {eyebrow}
          </div>
        </div>
      ) : hasAnyEyebrow ? (
        <div className="hidden sm:block min-h-[2rem] mb-3" />
      ) : null}
      
      {/* Metric content - aligned across all metrics */}
      <div className="flex flex-col">
        <div 
          className="stat-caption mb-2 whitespace-normal" 
          aria-live="polite" 
          aria-atomic="true"
        >
          {shouldAnimate ? `${prefix}${formatNumber(count)}${suffix}` : value}
        </div>
        <div className="body-default text-gray-700 max-w-[70%]">{description}</div>
      </div>
    </div>
  );
};

export default function ImpactMetrics({ metrics, className = "", columns = 4 }: ImpactMetricsProps) {
  const { ref } = useInView<HTMLElement>();
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const gridColsClass = columns === 3 
    ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3" 
    : "grid-cols-1 sm:grid-cols-2 impact-metrics-grid-4";
  
  // Check for reduced motion preference
  useEffect(() => {
    const checkReducedMotion = () => {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);
    };
    
    checkReducedMotion(); // Initial check
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };
    
    // Listen for changes (some browsers support this)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);
  
  // Check if any metric has an eyebrow
  const hasAnyEyebrow = metrics.some(metric => metric.eyebrow);
  
  return (
    // <section className={`-mx-6 px-6 py-12 md:-mx-12 md:px-12 ${className}`}>
    <section ref={ref} className={`animate-in-view py-4 ${className}`}>
      <div className={`grid ${gridColsClass} gap-8 w-full`}>
        {metrics.map((metric, index) => (
          <CountUpMetric
            key={index}
            value={metric.value}
            description={metric.description}
            index={index}
            eyebrow={metric.eyebrow}
            hasAnyEyebrow={hasAnyEyebrow}
            noAnimation={metric.noAnimation}
            prefersReducedMotion={prefersReducedMotion}
          />
        ))}
      </div>
    </section>
  );
}
